import React, { useState, useEffect } from 'react';
import { DropDownComponent, InputComponent, FileUploadComponent } from '../custcomponents';
import { useParams } from 'react-router-dom';
import DataTable from 'react-data-table-component';
import { toast } from 'react-toastify';
import { AxiosAdminInstance } from '../utils';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

interface Awardee {
    id: string;
    name: string;
    photoUrl: string;
}

interface Award {
    id: string;
    title: string;
    shortdesc: string;
    htmldesc: string;
    imageurl: string;
    awardees: Awardee[];
}

export const ManageAwards = () => {
    const { eventcode } = useParams<{ eventcode: string }>();
    const [awards, setAwards] = useState<Award[]>([]);
    const [loading, setLoading] = useState(false);
    const [editingAward, setEditingAward] = useState<Award | null>(null);
    const [newAward, setNewAward] = useState({
        title: '',
        shortdesc: '',
        htmldesc: '',
        imageFile: null as File | null,
        imageurl: '',
        contentType: ''
    });

    // Awardee state management
    const [awardees, setAwardees] = useState<Awardee[]>([]);
    const [editingAwardee, setEditingAwardee] = useState<Awardee | null>(null);
    const [newAwardee, setNewAwardee] = useState({
        name: '',
        photoFile: null as File | null,
        photoUrl: '',
        photoContentType: ''
    });

    useEffect(() => {
        fetchAwards();
    }, []);

    useEffect(() => {
        if (editingAward) {
            setNewAward({
                title: editingAward.title,
                shortdesc: editingAward.shortdesc,
                htmldesc: editingAward.htmldesc,
                imageFile: null,
                imageurl: editingAward.imageurl,
                contentType: ''
            });
            setAwardees(editingAward.awardees || []);
        } else {
            setNewAward({
                title: '',
                shortdesc: '',
                htmldesc: '',
                imageFile: null,
                imageurl: '',
                contentType: ''
            });
            setAwardees([]);
        }
    }, [editingAward]);

    useEffect(() => {
        if (editingAwardee) {
            setNewAwardee({
                name: editingAwardee.name,
                photoFile: null,
                photoUrl: editingAwardee.photoUrl,
                photoContentType: ''
            });
        } else {
            setNewAwardee({
                name: '',
                photoFile: null,
                photoUrl: '',
                photoContentType: ''
            });
        }
    }, [editingAwardee]);

    const fetchAwards = async () => {
        setLoading(true);
        try {
            const response = await AxiosAdminInstance.post('/api/admin/manageawards', {
                action: 'getAwards',
                data: { eventcode }
            });
            if (response.data.success) {
                const awardsObj = response.data.awards || {};
                const awardsArray = Object.keys(awardsObj).map(id => ({
                    id,
                    ...awardsObj[id]
                }));
                setAwards(awardsArray);
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            toast.error('Failed to fetch awards');
        }
        setLoading(false);
    };

    const fileToBase64 = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
                const base64 = reader.result as string;
                // Remove the data:image/jpeg;base64, prefix
                const base64Data = base64.split(',')[1];
                resolve(base64Data);
            };
            reader.onerror = error => reject(error);
        });
    };

    const handleAddAward = async () => {
        if (!newAward.title.trim()) {
            toast.error('Award title is required');
            return;
        }
        if (!newAward.shortdesc.trim()) {
            toast.error('Award short description is required');
            return;
        }
        setLoading(true);
        try {
            let data = {
                eventcode,
                title: newAward.title,
                shortdesc: newAward.shortdesc,
                htmldesc: newAward.htmldesc,
                awardees: awardees
            };
            
            if (editingAward) {
                data = { ...data, awardId: editingAward.id } as any;
            }
            
            if (newAward.imageFile) {
                const imageBase64 = await fileToBase64(newAward.imageFile);
                data = { ...data, imageBase64, imageFileName: newAward.imageFile.name, contentType: newAward.contentType } as any;
            }
            
            const response = await AxiosAdminInstance.post('/api/admin/manageawards', {
                action: editingAward ? 'editAward' : 'addAward',
                data
            });
            
            if (response.data.success) {
                toast.success(editingAward ? 'Award updated successfully' : 'Award added successfully');
                resetAwardForm();
                fetchAwards();
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            toast.error(editingAward ? 'Failed to update award' : 'Failed to add award');
        }
        setLoading(false);
    };

    const handleDeleteAward = async (awardId: string) => {
        if (!window.confirm('Are you sure you want to delete this award and all its awardees?')) return;
        setLoading(true);
        try {
            const response = await AxiosAdminInstance.post('/api/admin/manageawards', {
                action: 'deleteAward',
                data: { eventcode, awardId }
            });
            if (response.data.success) {
                toast.success('Award deleted successfully');
                fetchAwards();
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            toast.error('Failed to delete award');
        }
        setLoading(false);
    };

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>): Promise<boolean> => {
        const file = event.target.files?.[0];
        if (file) {
            // Validate file type
            if (!file.type.startsWith('image/')) {
                toast.error('Please select an image file');
                return false;
            }
            // Validate file size (100KB max)
            if (file.size > 100 * 1024) {
                toast.error('File size must be less than 100KB');
                return false;
            }
            setNewAward(prev => ({ ...prev, imageFile: file, imageurl: '', contentType: file.type }));
            return true;
        }
        return false;
    };

    const resetAwardForm = () => {
        setNewAward({
            title: '',
            shortdesc: '',
            htmldesc: '',
            imageFile: null,
            imageurl: '',
            contentType: ''
        });
        setAwardees([]);
        setEditingAward(null);
    };

    // Awardee management functions
    const handleAddAwardee = async () => {
        if (!newAwardee.name.trim()) {
            toast.error('Awardee name is required');
            return;
        }
        
        // Ensure we have an award to add the awardee to
        const targetAward = editingAward;
        if (!targetAward) {
            toast.error('Please select an award first');
            return;
        }
        
        setLoading(true);
        try {
            let data = {
                eventcode,
                awardId: targetAward.id,
                name: newAwardee.name
            };
            
            if (newAwardee.photoFile) {
                const photoBase64 = await fileToBase64(newAwardee.photoFile);
                data = { ...data, photoBase64, photoFileName: newAwardee.photoFile.name, photoContentType: newAwardee.photoContentType } as any;
            }
            
            const response = await AxiosAdminInstance.post('/api/admin/manageawards', {
                action: 'addAwardee',
                data
            });
            
            if (response.data.success) {
                toast.success('Awardee added successfully');
                // Refresh the current award to get updated awardees
                const awardResponse = await AxiosAdminInstance.post('/api/admin/manageawards', {
                    action: 'getAwards',
                    data: { eventcode }
                });
                const awardsObj = awardResponse.data.awards || {};
                const updatedAward = awardsObj[targetAward.id];
                if (updatedAward) {
                    setAwardees(updatedAward.awardees || []);
                }
                resetAwardeeForm();
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            toast.error('Failed to add awardee');
        }
        setLoading(false);
    };

    const handleEditAwardee = async (awardee: Awardee) => {
        setEditingAwardee(awardee);
        setNewAwardee({
            name: awardee.name,
            photoFile: null,
            photoUrl: awardee.photoUrl,
            photoContentType: ''
        });
    };

    const handleUpdateAwardee = async () => {
        if (!newAwardee.name.trim()) {
            toast.error('Awardee name is required');
            return;
        }
        
        // Ensure we have an award to update the awardee for
        const targetAward = editingAward;
        if (!targetAward) {
            toast.error('Please select an award first');
            return;
        }
        
        setLoading(true);
        try {
            let data = {
                eventcode,
                awardId: targetAward.id,
                awardeeId: editingAwardee?.id,
                name: newAwardee.name,
                currentPhotoUrl: editingAwardee?.photoUrl
            };
            
            if (newAwardee.photoFile) {
                const photoBase64 = await fileToBase64(newAwardee.photoFile);
                data = { ...data, photoBase64, photoFileName: newAwardee.photoFile.name, photoContentType: newAwardee.photoContentType } as any;
            }
            
            const response = await AxiosAdminInstance.post('/api/admin/manageawards', {
                action: 'editAwardee',
                data
            });
            
            if (response.data.success) {
                toast.success('Awardee updated successfully');
                // Refresh the current award to get updated awardees
                const awardResponse = await AxiosAdminInstance.post('/api/admin/manageawards', {
                    action: 'getAwards',
                    data: { eventcode }
                });
                const awardsObj = awardResponse.data.awards || {};
                const updatedAward = awardsObj[targetAward.id];
                if (updatedAward) {
                    setAwardees(updatedAward.awardees || []);
                }
                resetAwardeeForm();
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            toast.error('Failed to update awardee');
        }
        setLoading(false);
    };

    const handleDeleteAwardee = async (awardeeId: string) => {
        if (!window.confirm('Are you sure you want to delete this awardee?')) return;
        
        // Ensure we have an award to delete the awardee from
        const targetAward = editingAward;
        if (!targetAward) {
            toast.error('Please select an award first');
            return;
        }
        
        setLoading(true);
        try {
            const response = await AxiosAdminInstance.post('/api/admin/manageawards', {
                action: 'deleteAwardee',
                data: { eventcode, awardId: targetAward.id, awardeeId }
            });
            if (response.data.success) {
                toast.success('Awardee deleted successfully');
                // Refresh the current award to get updated awardees
                const awardResponse = await AxiosAdminInstance.post('/api/admin/manageawards', {
                    action: 'getAwards',
                    data: { eventcode }
                });
                const awardsObj = awardResponse.data.awards || {};
                const updatedAward = awardsObj[targetAward.id];
                if (updatedAward) {
                    setAwardees(updatedAward.awardees || []);
                }
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            toast.error('Failed to delete awardee');
        }
        setLoading(false);
    };

    const handleAwardeeFileChange = async (event: React.ChangeEvent<HTMLInputElement>): Promise<boolean> => {
        const file = event.target.files?.[0];
        if (file) {
            // Validate file type
            if (!file.type.startsWith('image/')) {
                toast.error('Please select an image file');
                return false;
            }
            // Validate file size (100KB max)
            if (file.size > 100 * 1024) {
                toast.error('File size must be less than 100KB');
                return false;
            }
            setNewAwardee(prev => ({ ...prev, photoFile: file, photoUrl: '', photoContentType: file.type }));
            return true;
        }
        return false;
    };

    const resetAwardeeForm = () => {
        setNewAwardee({
            name: '',
            photoFile: null,
            photoUrl: '',
            photoContentType: ''
        });
        setEditingAwardee(null);
    };

    const awardColumns = [
        { name: 'Actions', cell: (row: Award) => (
            <div>
                <button className="btn btn-sm btn-warning me-1" onClick={() => setEditingAward(row)} title="Edit">
                    <i className="fa fa-edit"></i>
                </button>
                <button className="btn btn-sm btn-info me-1" onClick={() => setEditingAward(row)} title="Manage Awardees">
                    <i className="fa fa-users"></i>
                </button>
                <button className="btn btn-sm btn-danger" onClick={() => handleDeleteAward(row.id)} title="Delete">
                    <i className="fa fa-trash"></i>
                </button>
            </div>
        ) },
        { name: 'Title', selector: (row: Award) => row.title, sortable: true },
        { name: 'Short Description', selector: (row: Award) => row.shortdesc, sortable: true },
        { name: 'Awardees Count', selector: (row: Award) => row.awardees?.length || 0, sortable: true },
        { name: 'Image', cell: (row: Award) => (
            row.imageurl ? <img src={row.imageurl} alt={row.title} style={{ width: '150px', height: '100px', objectFit: 'cover' }} /> : 'No Image'
        ) }
    ];

    const awardeeColumns = [
        { name: 'Actions', cell: (row: Awardee) => (
            <div>
                <button className="btn btn-sm btn-warning me-1" onClick={() => handleEditAwardee(row)} title="Edit">
                    <i className="fa fa-edit"></i>
                </button>
                <button className="btn btn-sm btn-danger" onClick={() => handleDeleteAwardee(row.id)} title="Delete">
                    <i className="fa fa-trash"></i>
                </button>
            </div>
        ) },
        { name: 'Name', selector: (row: Awardee) => row.name, sortable: true },
        { name: 'Photo', cell: (row: Awardee) => (
            row.photoUrl ? <img src={row.photoUrl} alt={row.name} style={{ width: '150px', height: '100px', objectFit: 'cover', borderRadius: '50%' }} /> : 'No Photo'
        ) }
    ];

    return (
        <>
            <section className="register-sec sec-padding-admin" data-scroll-index="8">
                <div className="container">
                    <div className="sec-title">
                        <span className="icon-inner wow flipInY" data-wow-duration="300ms">
                            <span className="fa-stack">
                                <i className="fa rhex fa-stack-2x"></i>
                                <i className="fa fa-trophy fa-stack-1x"></i>
                            </span>
                        </span>
                        <h3 className="wow fadeInRight" data-wow-duration="500ms">Manage Awards</h3>
                    </div>
                    <div className="register-sec-details">
                        <form onSubmit={(e) => { e.preventDefault(); handleAddAward(); }}>
                            <fieldset className="form-group p-3">
                                <legend className="w-auto px-2 subheading">{editingAward ? 'Edit Award' : 'Add New Award'}</legend>
                                <div className="row pt-2">
                                    <div className="col-lg-6 col-md-6 col-sm-12">
                                        <InputComponent
                                            custname="title"
                                            custplaceholder="Award Title"
                                            custid="awardTitle"
                                            onExternalValue={(val: string) => setNewAward(prev => ({ ...prev, title: val }))}
                                            onExternalBind={newAward.title}
                                        />
                                    </div>
                                    <div className="col-lg-6 col-md-6 col-sm-12">
                                        <InputComponent
                                            custname="shortdesc"
                                            custplaceholder="Short Description"
                                            custid="awardShortDesc"
                                            onExternalValue={(val: string) => setNewAward(prev => ({ ...prev, shortdesc: val }))}
                                            onExternalBind={newAward.shortdesc}
                                        />
                                    </div>
                                </div>
                                <div className="row pt-2">
                                    <div className="col-lg-12 col-md-12 col-sm-12">
                                        <label className="form-label">HTML Description</label>
                                        <ReactQuill
                                            value={newAward.htmldesc}
                                            onChange={(value) => setNewAward(prev => ({ ...prev, htmldesc: value }))}
                                            theme="snow"
                                            style={{ height: '200px', marginBottom: '50px' }}
                                        />
                                    </div>
                                </div>
                                <div className="row pt-2">
                                    <div className="col-lg-6 col-md-6 col-sm-12">
                                        <FileUploadComponent
                                            custname="image"
                                            custplaceholder="Upload Award Image"
                                            custid="awardImage"
                                            onChange={handleFileChange}
                                        />
                                        {newAward.imageurl && (
                                            <div className="mt-2">
                                                <img src={newAward.imageurl} alt="Current" style={{ width: '150px', height: '100px', objectFit: 'cover' }} />
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="row pt-2">
                                    <div className="col-lg-6 col-md-6 col-sm-6">
                                        <button className="btn btn-primary" type="submit" disabled={loading}>
                                            {editingAward ? 'Update Award' : 'Add Award'} {loading && <i className="fa fa-circle-o-notch fa-spin"></i>}
                                        </button>
                                    </div>
                                    <div className="col-lg-6 col-md-6 col-sm-6">
                                        {editingAward && (
                                            <button className="btn btn-secondary ms-2" type="button" onClick={resetAwardForm}>
                                                Cancel
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </fieldset>

                            {/* Combined Award & Awardees Management */}
                    {editingAward && (
                        <fieldset className="form-group p-3">
                            <legend className="w-auto px-2 subheading">Manage Awardees</legend>
                            <div className="row pt-2">
                                <div className="col-lg-4 col-md-6 col-sm-12">
                                    <InputComponent
                                        custname="awardeeName"
                                        custplaceholder="Awardee Name"
                                        custid="awardeeName"
                                        onExternalValue={(val: string) => setNewAwardee(prev => ({ ...prev, name: val }))}
                                        onExternalBind={newAwardee.name}
                                    />
                                </div>
                                <div className="col-lg-4 col-md-6 col-sm-12">
                                    <FileUploadComponent
                                        custname="awardeePhoto"
                                        custplaceholder="Upload Awardee Photo"
                                        custid="awardeePhoto"
                                        onChange={handleAwardeeFileChange}
                                    />
                                    {newAwardee.photoUrl && (
                                        <div className="mt-2">
                                            <img src={newAwardee.photoUrl} alt="Current" style={{ width: '150px', height: '100px', objectFit: 'cover', borderRadius: '50%' }} />
                                        </div>
                                    )}
                                </div>
                                <div className="col-lg-4 col-md-12 col-sm-12">
                                    <button className="btn btn-success me-2" type="button" onClick={editingAwardee ? handleUpdateAwardee : handleAddAwardee} disabled={loading}>
                                        {editingAwardee ? 'Update Awardee' : 'Add Awardee'} {loading && <i className="fa fa-circle-o-notch fa-spin"></i>}
                                    </button>
                                    {editingAwardee && (
                                        <button className="btn btn-secondary me-2" type="button" onClick={resetAwardeeForm}>
                                            Cancel
                                        </button>
                                    )}
                                    <button className="btn btn-secondary" type="button" onClick={() => {
                                        setEditingAward(null);
                                    }}>
                                        Close
                                    </button>
                                </div>
                            </div>
                            <div className="row pt-3">
                                <div className="col-lg-12 col-md-12 col-sm-12">
                                    <DataTable
                                        columns={awardeeColumns}
                                        data={awardees}
                                        customStyles={{
                                            cells: {
                                                style: {
                                                    wordWrap: 'break-word',
                                                    whiteSpace: 'normal',
                                                    padding: '8px'
                                                }
                                            }
                                        }}
                                    />
                                </div>
                            </div>
                        </fieldset>
                    )}

                            <fieldset className="form-group p-3">
                                <legend className="w-auto px-2 subheading">Awards</legend>
                                <div className="row pt-2">
                                    <div className="col-lg-12 col-md-12 col-sm-12">
                                        <DataTable
                                            columns={awardColumns}
                                            data={awards}
                                            customStyles={{
                                                cells: {
                                                    style: {
                                                        wordWrap: 'break-word',
                                                        whiteSpace: 'normal',
                                                        padding: '8px'
                                                    }
                                                }
                                            }}
                                        />
                                    </div>
                                </div>
                            </fieldset>
                        </form>
                    </div>
                </div>
            </section>
        </>
    );
};